// FICTIONAL sample stories for local development and the emulator seed.
// Headlines, dates, and statuses are invented. They are NOT real Databricks
// announcements and must always render with a visible "Sample" marker.
import type { AudienceTag, ContentType, Story, Topic } from "./model.ts";
import type { FlagStatus } from "./flags.ts";

const SAMPLE_SOURCE_URL = "https://docs.databricks.com/aws/en/release-notes/";

interface SampleInput {
  slug: string;
  title: string;
  topic: Topic;
  tags: string[];
  audiences: AudienceTag[];
  type: ContentType;
  daysAgo: number; // dates are relative to today so "New" flags stay meaningful
  products?: string[];
  /** Fictional status label, rendered with a visible sample marker. */
  label?: FlagStatus;
  minutes: number;
  plain: string;
  business: string;
  technical: string;
  why: string;
}

function sample(i: SampleInput): Story {
  const day = 86_400_000;
  const ts = Math.floor(Date.now() / day) * day - i.daysAgo * day + 15 * 3_600_000;
  return {
    id: `sample-${i.slug}`,
    title: i.title,
    canonicalUrl: SAMPLE_SOURCE_URL,
    sourceId: "sample",
    sourceName: "Sample data (fictional)",
    contentType: i.type,
    primaryTopic: i.topic,
    topicTags: i.tags,
    audiences: i.audiences,
    publishedAt: ts,
    sourceUpdatedAt: null,
    sortAt: ts,
    summaries: { plainEnglish: i.plain, businessImpact: i.business, technicalDetail: i.technical },
    whyItMatters: i.why,
    estimatedReadMinutes: i.minutes,
    releaseStatus: "unknown",
    clouds: [],
    availabilityNotes: null,
    availabilityVerifiedAt: null,
    availabilitySourceUrl: null,
    sourceLinks: [
      { label: "Browse official Databricks release notes", url: SAMPLE_SOURCE_URL, type: "docs" },
    ],
    summaryProvenance: "sample",
    isSample: true,
    products: i.products ?? [],
    docsLabel: i.label ? { status: i.label, sourceUrl: SAMPLE_SOURCE_URL } : null,
    docsClouds: [],
  };
}

export const SAMPLE_STORIES: Story[] = [
  sample({
    slug: "agent-eval-scorecards",
    products: ["mlflow", "agent-bricks"],
    label: "beta",
    title: "Agent evaluation scorecards you can share with reviewers",
    topic: "ai_agents",
    tags: ["agents", "evaluation", "mlflow"],
    audiences: ["technical", "business"],
    type: "release_note",
    daysAgo: 4,
    minutes: 3,
    plain:
      "Teams building AI assistants can now produce a single scorecard that shows how well an assistant answered a set of test questions. Reviewers who do not write code can open the scorecard, see where answers were weak, and leave comments, so quality checks stop living only in a developer's notebook.",
    business:
      "A shareable scorecard turns AI quality into something product owners and risk reviewers can sign off on. That shortens the approval loop for internal assistants, makes it easier to compare vendors or model choices, and gives leadership a concrete artifact instead of anecdotes when deciding whether an assistant is ready for customers.",
    technical:
      "Evaluation runs are summarized into a persisted scorecard object linked to the run's traces. Each judged question keeps its inputs, retrieved context, judge rationale, and score, and reviewers' labels are written back as feedback on the trace. Scorecards can be diffed across runs, so a regression in retrieval or prompt changes shows up per question.",
    why: "It gives non-engineers a real seat in deciding whether an AI assistant is good enough to ship.",
  }),
  sample({
    slug: "agent-tool-permissions",
    products: ["unity-gateway", "agent-bricks"],
    label: "public_preview",
    title: "Tool permissions for agents follow the same rules as people",
    topic: "ai_agents",
    tags: ["agents", "security", "tools"],
    audiences: ["technical", "business"],
    type: "announcement",
    daysAgo: 11,
    minutes: 4,
    plain:
      "When an AI agent looks something up or takes an action, it now has to follow the same access rules as the person using it. If you cannot see a table, the agent working for you cannot see it either. This makes agents behave more like a careful colleague than a master key.",
    business:
      "Treating agents like users removes one of the biggest blockers to rolling them out: fear that an assistant will leak data across teams. Security teams can reuse existing permission reviews rather than inventing a parallel process, which lowers the cost of saying yes to new agent projects.",
    technical:
      "Agent tool calls execute with on-behalf-of credentials, so table, volume, and function grants are evaluated against the end user's identity rather than a shared service principal. Denied calls surface as structured tool errors the agent can reason about, and every call is attributed in audit logs with both the agent and user identity.",
    why: "Agents that inherit your permissions are far easier for security teams to approve.",
  }),
  sample({
    slug: "agent-starter-templates",
    products: ["agent-bricks", "vector-search"],
    title: "Starter templates for customer-support assistants",
    topic: "ai_agents",
    tags: ["agents", "templates", "getting-started"],
    audiences: ["new_to_databricks", "business"],
    type: "blog",
    daysAgo: 23,
    minutes: 6,
    plain:
      "A new set of starter templates shows how to build a simple customer-support assistant from your own help articles. Each template walks through loading documents, asking questions, and checking the answers, so a small team can try the idea in an afternoon instead of starting from a blank page.",
    business:
      "Templates make it cheaper to run a small, low-risk pilot before committing budget. A support leader can see a working example with their own content, judge whether answers are useful, and decide on next steps with real evidence rather than a slide deck or a vendor demo.",
    technical:
      "Each template ships as a bundle with an ingestion job for help-center content, a vector index, a retrieval chain, and an evaluation set. Parameters cover chunk size, embedding endpoint, and prompt. The evaluation step runs automatically after deployment, so the first run already produces a baseline to compare against.",
    why: "It lowers the cost of finding out whether an AI assistant is worth building at all.",
  }),
  sample({
    slug: "pipeline-step-retry",
    products: ["lakeflow-jobs"],
    label: "ga",
    title: "Pipelines can retry a single failed step instead of the whole run",
    topic: "data_engineering",
    tags: ["pipelines", "reliability", "jobs"],
    audiences: ["technical"],
    type: "release_note",
    daysAgo: 6,
    minutes: 2,
    plain:
      "When one step in a data pipeline fails, you can now rerun just that step and the ones after it. Before, many teams restarted everything from the beginning, which wasted time and money. This is like fixing one broken link in a chain rather than rebuilding the chain.",
    business:
      "Partial reruns shorten recovery time after overnight failures, which means dashboards and reports are ready sooner for the business. They also reduce wasted compute spend on work that already succeeded, a small saving per incident that adds up for teams running hundreds of pipelines.",
    technical:
      "Run state is checkpointed per task, so a repair run resumes from the failed task and its downstream dependents while reusing outputs of succeeded upstream tasks. Parameters can be overridden for the repair. Lineage records both the original and repair run IDs, and alerts can distinguish first-attempt failures from repaired ones.",
    why: "Faster recovery from failures means fresher data for everyone downstream.",
  }),
  sample({
    slug: "streaming-cost-preview",
    products: ["streaming", "lakeflow-pipelines"],
    title: "See the cost of a streaming job before you turn it on",
    topic: "data_engineering",
    tags: ["streaming", "cost", "planning"],
    audiences: ["technical", "business"],
    type: "blog",
    daysAgo: 18,
    minutes: 5,
    plain:
      "Streaming jobs process data continuously, which can make their cost hard to predict. A new planning view estimates what a streaming job might cost per day based on its expected data volume, so teams can talk about budget before anything is running rather than after the first bill arrives.",
    business:
      "Up-front estimates make real-time projects easier to approve because finance can see a range before committing. They also help teams choose between real-time and scheduled processing on cost grounds, which is often the right conversation for use cases where minutes of delay are acceptable.",
    technical:
      "The planner samples recent input rates, applies the job's trigger interval and state size, and projects compute usage across a configurable volume range. Estimates are labeled as projections with stated assumptions. After launch, actual usage is plotted against the projection so teams can recalibrate inputs.",
    why: "Budget conversations get easier when there is a number on the table before launch.",
  }),
  sample({
    slug: "ingest-connectors-roundup",
    products: ["lakeflow-connect"],
    title: "Roundup: three new ways to bring SaaS data in without code",
    topic: "data_engineering",
    tags: ["ingestion", "connectors", "no-code"],
    audiences: ["new_to_databricks", "business"],
    type: "blog",
    daysAgo: 32,
    minutes: 7,
    plain:
      "This roundup explains three point-and-click ways to copy data from common business apps, such as CRM and HR tools, into one place for analysis. Each option is described in plain terms with when to use it, so you can pick the simplest path that fits your situation.",
    business:
      "Fewer custom integrations mean fewer fragile scripts that only one person understands. Business teams can get reporting data sooner, and IT can standardize on managed connectors that are easier to monitor, secure, and hand over when people change roles or teams reorganize.",
    technical:
      "The post compares managed connectors, partner integrations, and file-based landing zones on incremental sync support, schema evolution handling, and monitoring. It includes a decision table covering expected volume, freshness needs, and whether change data capture is available from the source system.",
    why: "Picking the simplest ingestion path avoids years of maintaining custom glue code.",
  }),
  sample({
    slug: "dashboard-natural-language",
    products: ["aibi-dashboards", "genie"],
    label: "public_preview",
    title: "Ask follow-up questions directly from a dashboard tile",
    topic: "analytics_bi",
    tags: ["dashboards", "natural-language", "self-service"],
    audiences: ["business", "new_to_databricks"],
    type: "announcement",
    daysAgo: 8,
    minutes: 3,
    plain:
      "While looking at a chart on a dashboard, you can now ask a question in everyday words, such as why sales dipped last week, and get an answer that uses the same data as the chart. You stay in the dashboard instead of asking an analyst to build a new report.",
    business:
      "Letting viewers ask their own follow-up questions reduces the queue of small requests that slows analytics teams. Decision makers get answers while the question is still relevant, and analysts spend more time on deeper work instead of one-off chart tweaks.",
    technical:
      "Follow-up questions are scoped to the tile's dataset and filters, and generated SQL runs under the viewer's permissions. The generated query is shown for inspection. Curated instructions and example queries attached to the dataset steer generation, and feedback on answers is logged for authors to review.",
    why: "It shortens the gap between seeing a number and understanding it.",
  }),
  sample({
    slug: "metric-definitions-shared",
    products: ["metric-views", "databricks-sql"],
    label: "ga",
    title: "Define a metric once and reuse it across every dashboard",
    topic: "analytics_bi",
    tags: ["metrics", "semantic-layer", "consistency"],
    audiences: ["business", "technical"],
    type: "release_note",
    daysAgo: 29,
    minutes: 3,
    plain:
      "Teams often calculate the same number, like monthly active customers, in slightly different ways. Now a metric can be defined once and reused everywhere, so two dashboards showing the same metric should agree. If the definition changes, every place that uses it updates together.",
    business:
      "Consistent metrics end the meetings where people argue about whose number is right. Leaders can trust that a KPI means the same thing in every report, and finance and operations can align on definitions that are owned and reviewed rather than copied around.",
    technical:
      "Metric definitions are stored as governed objects with measures, dimensions, and joins declared in YAML. Queries reference the metric by name and are expanded at query time, so the calculation is not duplicated in dashboard SQL. Definitions carry owners, comments, and permissions like any other catalog object.",
    why: "When everyone uses the same definition, meetings move from arguing about numbers to acting on them.",
  }),
  sample({
    slug: "governance-tag-policies",
    products: ["unity-catalog"],
    label: "beta",
    title: "Tag a column as sensitive once and masking follows it",
    topic: "governance",
    tags: ["governance", "masking", "tags"],
    audiences: ["technical", "business"],
    type: "release_note",
    daysAgo: 3,
    minutes: 4,
    plain:
      "Instead of setting privacy rules table by table, administrators can label a column as sensitive, such as a phone number, and the matching rule applies wherever that label appears. New tables with the same label are protected automatically, which reduces the chance of something slipping through.",
    business:
      "Tag-based protection scales with the data estate instead of with headcount. Compliance teams get a clearer story for auditors, and data owners can share more data safely because sensitive fields stay masked for people who should not see them, even as new tables are added.",
    technical:
      "Policies bind to governed tags rather than to individual columns, and are evaluated at query time against the caller's group membership. Tag assignments propagate through lineage-aware suggestions but require explicit approval. Policy evaluation results are captured in audit events for review.",
    why: "Protection that follows the data is much harder to forget on the next new table.",
  }),
  sample({
    slug: "governance-lineage-for-auditors",
    products: ["unity-catalog"],
    title: "A plain-language lineage view built for auditors",
    topic: "governance",
    tags: ["lineage", "audit", "compliance"],
    audiences: ["business", "new_to_databricks"],
    type: "blog",
    daysAgo: 44,
    minutes: 6,
    plain:
      "Lineage shows where a number came from and where it goes next. This post describes a simpler lineage view aimed at auditors and managers, which uses plain labels and hides technical detail by default, so people can follow a report back to its sources without reading code.",
    business:
      "Audits move faster when reviewers can answer their own provenance questions. Teams spend less time assembling evidence spreadsheets, and organizations in regulated industries can demonstrate control over critical reports with far less disruption to the analysts and engineers who build and maintain them.",
    technical:
      "The view renders column-level lineage collapsed to business-friendly nodes with owners and descriptions, while keeping full job, notebook, and query detail available on expansion. Exported evidence packages include a timestamped graph snapshot and the access grants that applied at that time.",
    why: "Being able to prove where a number came from is often what auditors need most.",
  }),
  sample({
    slug: "platform-serverless-defaults",
    products: ["serverless"],
    label: "ga",
    title: "New workspaces start with serverless compute by default",
    topic: "platform",
    tags: ["serverless", "compute", "admin"],
    audiences: ["new_to_databricks", "technical"],
    type: "announcement",
    daysAgo: 13,
    minutes: 3,
    plain:
      "New workspaces can begin with compute that starts on demand and shuts down on its own, so people do not have to choose machine sizes before running their first query. It is meant to make the first day simpler, while still letting administrators set limits.",
    business:
      "Removing setup choices helps new teams become productive in days instead of weeks. Idle compute that turns itself off also reduces surprise costs from forgotten clusters, which is one of the most common early budget complaints from finance teams in the first months.",
    technical:
      "Notebooks, jobs, and SQL default to serverless compute in newly created workspaces, with budget policies and usage tags applied at the workspace level. Classic compute remains available. Admins can restrict instance types, set spending alerts, and review usage through system tables.",
    why: "A simpler first day makes it more likely a new team gets real value quickly.",
  }),
  sample({
    slug: "platform-cli-bundles",
    products: ["developer-tools"],
    label: "private_preview",
    title: "Deploying projects from the command line gets a guided mode",
    topic: "platform",
    tags: ["cli", "bundles", "devops"],
    audiences: ["technical"],
    type: "release_note",
    daysAgo: 25,
    minutes: 2,
    plain:
      "Engineers can now use a guided, step-by-step mode when packaging and deploying their projects from the command line. It asks a few questions and explains each choice, which helps people who are new to deployment avoid common mistakes without reading long documentation first.",
    business:
      "Standardized deployments reduce the number of one-off setups that are hard to support. Teams can move work from development to production more predictably, which lowers operational risk, shortens release cycles, and makes it easier to onboard new engineers onto existing projects.",
    technical:
      "The guided mode scaffolds a bundle configuration with targets, variables, and permissions, validates it against the workspace, and previews the planned changes before deploying. Generated configuration is plain YAML checked into version control, so CI pipelines can run the same deployment non-interactively.",
    why: "Predictable deployments are what turn a promising prototype into a supported service.",
  }),
  sample({
    slug: "business-ops-forecasting",
    products: ["lakeflow-jobs", "aibi-dashboards"],
    title: "How an operations team cut weekly forecasting from days to hours",
    topic: "business_impact",
    tags: ["case-study", "forecasting", "operations"],
    audiences: ["business", "new_to_databricks"],
    type: "blog",
    daysAgo: 16,
    minutes: 8,
    plain:
      "This story follows a fictional operations team that used to spend most of each week stitching together spreadsheets for a demand forecast. By moving the data into one place and automating the forecast, they produced the same weekly view in a few hours and spent the saved time on planning.",
    business:
      "The example shows a common pattern: the first return on a data platform often comes from removing manual preparation work, not from advanced AI. Time saved goes back to decision making, and having one shared forecast reduces disagreements between planning and finance.",
    technical:
      "The workflow lands ERP and order data into managed tables, builds features with scheduled jobs, and trains a forecasting model per product family. Forecasts are written back to a table that feeds a dashboard and an export for the planning tool. Accuracy is tracked against actuals each week.",
    why: "It shows that big wins often start with removing manual work, not with advanced AI.",
  }),
  sample({
    slug: "business-ai-roi-framework",
    products: [],
    title: "A simple framework for estimating the return on an AI project",
    topic: "business_impact",
    tags: ["roi", "strategy", "ai"],
    audiences: ["business"],
    type: "blog",
    daysAgo: 37,
    minutes: 7,
    plain:
      "Before starting an AI project, it helps to write down what will change if it works, how you will measure it, and what it will cost. This post offers a one-page framework with four questions, so teams can compare ideas fairly and drop the ones that will not pay off.",
    business:
      "A shared framework lets leaders compare AI proposals on the same terms and fund the ones with clear measures of success. It also sets expectations early, which reduces the risk of pilots that never end because nobody agreed on what success looks like.",
    technical:
      "The framework maps each use case to a baseline metric, an expected effect range, data readiness, and run cost drivers such as inference volume and retrieval size. It suggests instrumenting evaluation and usage logging from the first prototype so the estimate can be checked against real numbers.",
    why: "Clear success measures are the difference between a pilot and a product.",
  }),
  sample({
    slug: "platform-region-expansion",
    products: [],
    title: "Sample of an availability update with no verified details",
    topic: "platform",
    tags: ["regions", "availability"],
    audiences: ["technical", "business"],
    type: "announcement",
    daysAgo: 50,
    minutes: 2,
    plain:
      "This sample shows how Databricks Pulse handles an update about where a feature is available. Because nothing here has been checked against official documentation, the card shows no release status or cloud labels. Real stories show those labels only after an editor confirms them in official docs.",
    business:
      "Availability claims drive real decisions, such as whether a team can plan a launch in a given region. Showing labels only after verification avoids building plans on assumptions, and links to the authoritative documentation so readers can check details themselves.",
    technical:
      "Stories carry releaseStatus, clouds, availabilityVerifiedAt, and availabilitySourceUrl. The UI renders status and cloud labels only when a verification timestamp and an https documentation URL are both present. Ingestion never derives these fields from titles or marketing text; they default to unknown.",
    why: "Unverified availability claims are worse than none, so this app shows none.",
  }),
  sample({
    slug: "notebook-legacy-export",
    products: ["notebooks"],
    label: "deprecated",
    title: "An older notebook export format is being phased out",
    topic: "platform",
    tags: ["notebooks", "deprecation", "migration"],
    audiences: ["technical"],
    type: "release_note",
    daysAgo: 9,
    minutes: 2,
    plain:
      "One of the older ways to save notebooks as files is being phased out over the coming months. Notebooks you already exported keep working, but new exports should use the current format. Teams that rely on scripts built around the old format have time to switch before it stops being available.",
    business:
      "A deprecation notice is a planning signal rather than an emergency. Owners of affected automation can schedule the change into normal work, avoid a last-minute scramble, and confirm that backups and audit copies of notebooks will remain readable after the old format is no longer offered.",
    technical:
      "Export calls requesting the legacy format return a deprecation warning and will later be rejected. The current source format round-trips cells, widgets, and magic commands, and the CLI and REST API accept it today. Scripts should switch the requested format parameter and re-run any validation that inspects exported files.",
    why: "Deprecations give you time to change on your schedule instead of during an outage.",
  }),
  sample({
    slug: "model-retired",
    products: ["foundation-models", "model-serving"],
    label: "retired",
    title: "An older hosted chat model has been retired",
    topic: "ai_agents",
    tags: ["models", "retirement", "serving"],
    audiences: ["technical", "business"],
    type: "release_note",
    daysAgo: 5,
    minutes: 2,
    plain:
      "One of the older hosted chat models is no longer available, after an earlier notice. Apps and agents that still pointed at it will get errors until they are switched to a newer model. Most teams only need to change the model name in one place and check that answers still look right.",
    business:
      "Model retirements are a normal part of using hosted AI, and they are a reminder to keep a list of which assistants depend on which model. Teams with that list can switch quickly and re-check quality, while teams without it discover the dependency when something breaks for users.",
    technical:
      "Requests to the retired endpoint name now fail with a not-found error. Update the model reference in serving endpoint configs, agent code, and AI Functions calls, then re-run evaluation sets to compare quality and latency. Gateway usage logs can reveal any remaining callers that still reference the old name.",
    why: "Knowing which apps depend on which model turns a retirement into a quick swap.",
  }),
];
